import React, { useEffect, useMemo, useState } from 'react';
import '../../ApplicationsDashboard/ApplicationsDashboard.css';
import './LeadReports.css';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../components/Sidebar/Sidebar';
import SeedsLogo from "../../../assets/images/SeedsLogo.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faBell,
    faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import UserProfileDropdown from '../../../components/UserProfile/UserProfile';
import CustomPagination from '../../../components/Pagination/CustomPagination';
import { useDispatch, useSelector } from 'react-redux';
import { getLeadReport } from '../../../redux/ReportsReducer/action';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { downloadReport, resetFilters } from '../../../utils/reportUtils';
import DownloadReport from '../DownloadReport';
import FunnelIconSvg from '../../../components/SVG/FunnelIconSvg';
import FilterModal from '../FilterModal/FilterModal';
import { REPORTS_COMPONENTS } from '../../../config/reports';
import { toFilterArray } from '../../../config/lead';
import useBranchOptions from '../../../hooks/useBranchOptions';
import { isAuthorised } from '../../../utils/common';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';

const LeadReports = () => {
    
    let tokens = JSON.parse(sessionStorage.getItem("tokens")) || null;
    let authToken = tokens?.token || null;

    const { leadReports, pagination, officerProfile } = useSelector((store) => {
    return {
      leadReports: store.ReportsReducer.leadReports.data,
      pagination: store.ReportsReducer.leadReports.pagination,
      officerProfile: store.UserReducer.officerProfile
    }
  }, []);
    
    const leads = [];

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const storedFilters = JSON.parse(sessionStorage.getItem('leadFilters')) || {};
    
    const [leadFilters, setLeadFilters] = useState({
        customer_name: storedFilters?.customer_name || "",
        lead_id: storedFilters?.lead_id || "",
        // Multi-value filters. toFilterArray tolerates the plain strings an
        // older build persisted under these keys.
        branch_name: toFilterArray(storedFilters?.branch_name),
        status: toFilterArray(storedFilters?.status),
        created_from: storedFilters?.created_from || "",
        created_to: storedFilters?.created_to || "",
        global_search: storedFilters?.global_search || ""
    });

    // Fills the Branch field in REPORT_FILTERS.leads, which ships with empty
    // options because the branch list is fetched at runtime.
    const { branchOptions, normalizeBranchValues } = useBranchOptions();
    const branchFilterOptions = useMemo(
        () => ({ branch_name: branchOptions }),
        [branchOptions],
    );

    // Realign a branch filter restored from sessionStorage with the option
    // values once the branch list arrives, so it shows as ticked and can be
    // unticked. Returning `prev` unchanged keeps this from looping.
    useEffect(() => {
        setLeadFilters((prev) => {
            const next = normalizeBranchValues(prev.branch_name);
            const current = Array.isArray(prev.branch_name) ? prev.branch_name : [];
            const unchanged =
                next.length === current.length &&
                next.every((v, i) => v === current[i]);
            return unchanged ? prev : { ...prev, branch_name: next };
        });
    }, [normalizeBranchValues]);

    const [currentPage, setCurrentPage] = useState(parseInt(sessionStorage.getItem('reportsCurrentPage')) || 1);
    const [limit, setLimit] = useState(parseInt(sessionStorage.getItem('reportsLimit')) || 10);
    const totalCount = parseInt(sessionStorage.getItem('reportsTotalCount')) || 0;
    const totalPages = Math.ceil(totalCount / limit);

    const [dateRange, setDateRange] = useState([
        leadFilters?.created_from ? new Date(leadFilters.created_from) : null,
        leadFilters?.created_to ? new Date(leadFilters.created_to) : null,
    ]);

    const [startDate, endDate] = dateRange;

    useEffect(() => {
        dispatch(getLeadReport(currentPage, limit, leadFilters, null));

        sessionStorage.setItem('reportsCurrentPage', currentPage);
        sessionStorage.setItem('reportsLimit', limit);
        sessionStorage.setItem('leadFilters', JSON.stringify(leadFilters));
    // }, [dispatch, limit, currentPage, leadFilters]);
    }, [dispatch, limit, currentPage]);

    useEffect(() => {
        return () => {
            sessionStorage.removeItem("leadFilters");
            sessionStorage.removeItem("reportsCurrentPage");
            sessionStorage.removeItem("reportsLimit");
            sessionStorage.removeItem("reportsTotalPages");
            sessionStorage.removeItem("reportsTotalCount");
        };
    }, []);

    const handlePageChange = (newPage) => {
        // Ensure we're working with 1-based pagination
        const pageToSet = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(pageToSet);
        sessionStorage.setItem('reportsCurrentPage', pageToSet.toString());
    };

    const formatDate = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleDateRangeChange = (update) => {
        setDateRange(update);

        const [start, end] = update;

        const updatedFilters = {
            ...leadFilters,
            created_from: formatDate(start),
            created_to: formatDate(end),
        };

        setLeadFilters(updatedFilters);
        sessionStorage.setItem("leadFilters", JSON.stringify(updatedFilters));
    };

    const handleFilterChange = (e) => {
        let {name, value} = e.target;

        if (typeof value === 'string') {
            value = value.replace(/[\t\n\r]+/g, '').replace(/^\s+/, '');
        }

        let updatedFilters = {
            ...leadFilters,
            [name]: value
        };

        setLeadFilters(updatedFilters);
        handlePageChange(1);
        sessionStorage.setItem("leadFilters", JSON.stringify(updatedFilters));
    }

    return (
        <>
        <div className="reportsDashboardContainer">
            <div className="reportsDashboard">
                <Sidebar />

                <div className="dashboardMainContent">
                    <div className="contentHeader">
                        <div className="userLogo">
                            <img src={SeedsLogo} alt="logo" className="SeedsLogo" />
                        </div>
                        <div className="headerRightPanel">
                            {/* <div className="headerSearchBar">
                                <input type="text" placeholder="Search" />
                                <FontAwesomeIcon icon={faSearch} />
                            </div>
                            <div className="headerNotifications">
                                <FontAwesomeIcon icon={faBell} />
                                <span className="notificationBadge">9+</span>
                            </div>
                            <div className="headerHelp">
                                <FontAwesomeIcon icon={faQuestionCircle} />
                            </div> */}
                            <UserProfileDropdown />
                        </div>
                    </div>

                    <div className="pageContentWrapper">
                        <div className="pageHeaderSection">
                        <Breadcrumb items={[
                            { label: 'Admin' },
                            { label: 'Reports', onClick: () => navigate('/reports') },
                            { label: 'Leads' }
                        ]} />

                        <div className="pageActions">
                            <h2>Leads</h2>
                        </div>
                    </div>

                   <div className="searchControlSection">
    <div className="searchGroup">
        {isAuthorised(
            officerProfile,
            REPORTS_COMPONENTS['leads_search']
        ) && <div className="tableSearchBar">
            <FontAwesomeIcon icon={faSearch} />
            <input 
                // type="text" 
                type="search"
                placeholder="Search" 
                name="global_search"
                value={leadFilters?.global_search} 
                onChange={handleFilterChange}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        dispatch(getLeadReport(1, limit, leadFilters, null));
                        handlePageChange(1);
                    }
                }}
                onInput={(e) => {
                    if (e.target.value === "") {
                        let updatedFilters = {
                            ...leadFilters,
                            global_search: e.target.value
                        }

                        setLeadFilters(updatedFilters)

                        dispatch(getLeadReport(1, limit, updatedFilters));
                        handlePageChange(1);
                    }
                }}
            />
        </div>}
        <div className="searchActionButtons">
            {isAuthorised(
                officerProfile,
                REPORTS_COMPONENTS['leads_search']
            ) && <button 
                className="searchButton"
                onClick={() => {
                    setCurrentPage(1);
                    dispatch(getLeadReport(1, limit, leadFilters, null));
                }}
            >
                 <FontAwesomeIcon icon={faSearch} />
            </button>}
            {isAuthorised(
                officerProfile,
                REPORTS_COMPONENTS['leads_download']
            ) && <button 
                className="downloadButton"
                // onClick={() => downloadReportByDateRange("leads", leadFilters?.created_from, leadFilters?.created_to)}
                // onClick={() => setShowDownloadModal(true)}
                onClick={() => {
                    downloadReport("leads", leadFilters, setIsDownloading);
                }}
                disabled={isDownloading}
            >
                {isDownloading ? (
                    <>
                        <span className="buttonSpinner"></span>
                        Downloading...
                    </>
                ) : (
                    <>
                        Download
                    </>
                )}
            </button>}
            {isAuthorised(
                officerProfile,
                REPORTS_COMPONENTS['leads_filters']
            ) && <button 
                className="filterButton"
                onClick={() => setShowFilterModal(true)}
            >
                <FunnelIconSvg fillColor={"black"}/>
            </button>}
        </div>
    </div>
</div>

                    {
                        showFilterModal && (
                            <FilterModal 
                                reportTitle={"leads"}
                                dynamicOptions={branchFilterOptions}
                                filters={leadFilters}
                                setFilters={setLeadFilters}
                                resetFilters={() => {
                                    let resettedFilters = resetFilters(leadFilters);

                                    setLeadFilters(resettedFilters);
                                    dispatch(getLeadReport(1, limit, resettedFilters, null));
                                    handlePageChange(1);
                                    setShowFilterModal(false);
                                    sessionStorage.setItem("leadFilters", JSON.stringify(resettedFilters));
                                    return resettedFilters;
                                }}
                                handleApplyFilters={(finalFilters) => {
                                    handlePageChange(1);
                                    dispatch(getLeadReport(1, limit, finalFilters, null));
                                    setShowFilterModal(false);
                                }}
                                onClose={() => {
                                    setShowFilterModal(false)
                                }}
                            />
                        )
                    }

                    <div className="tableCard">
                        <main className="">
                            <div className="reportsTableContainer">
                                <table className="applications-table" style={{ overflowX: "auto" }}>
                                <thead>
                                    <tr>
                                        <th>Lead ID</th>
                                        <th>Customer Name</th>
                                        <th>Branch</th>
                                        <th>Status</th>
                                        <th>Last Follow-up Date</th>
                                        <th>Lead Creation Date</th>
                                        <th>Assigned To</th>
                                        <th>Shop Name</th>
                                        <th>Mobile No.</th>
                                        <th>Created By</th>
                                    </tr>
                                    {/* <tr className="filter-row">
                                        <td>
                                            <input
                                                type="text"
                                                name="lead_id"
                                                value={leadFilters?.lead_id || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                name="customer_name"
                                                value={leadFilters?.customer_name || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                name="branch_name"
                                                value={leadFilters?.branch_name || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                name="status"
                                                value={leadFilters?.status || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td></td>
                                        <td>
                                            <DatePicker
                                                selectsRange
                                                startDate={startDate}
                                                endDate={endDate}
                                                onChange={handleDateRangeChange}
                                                isClearable
                                                placeholderText="Select date range"
                                                className="form-control form-control-sm date-picker-filter"
                                            />
                                        </td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr> */}
                                </thead>
                                <tbody>
                                    {
                                        leadReports?.length === 0 ? 
                                        <tr>
                                            <td colSpan={10} className="no-data-cell">
                                                No records found
                                            </td>
                                        </tr> :
                                        leadReports?.map((lead, index) => (
                                            <tr key={index}>
                                                <td>{lead?.lead_id}</td>
                                                <td>{lead?.customer_name}</td>
                                                <td>{lead?.branch_name}</td>
                                                <td>{lead?.status}</td>
                                                <td>{lead?.follow_up_date}</td>
                                                <td>{lead?.created_at}</td>
                                                <td>{lead?.assigned}</td>
                                                <td>{lead?.shop_name}</td>
                                                <td>{lead?.phone_number}</td>
                                                <td>{lead?.created_by}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>

                        {leadReports?.length > 0 && <div className="paginationControls">
                            <CustomPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>}
                    </main>
                </div>
            </div>
        </div>
            </div>
        </div>

        {
            showDownloadModal && (
                <DownloadReport 
                    isOpen={showDownloadModal}
                    onClose={() => setShowDownloadModal(false)}
                    reportTitle={"leads"}
                />
            )
        }

        
        </>
    )
}

export default LeadReports